import { COUNCILS, getCouncilConfig, isLegacyCouncil } from "./councils";

export const PLANNING_ARCHIVE_PAGE_SIZE = 200;
export const PLANNING_ARCHIVE_REVALIDATE_SECONDS = 6 * 60 * 60;
// Edinburgh keeps the original, council-less archive URLs (/planning-archive, /planning-archive/2).
export const PLANNING_ARCHIVE_DEFAULT_COUNCIL = "edinburgh";

export function parsePlanningArchivePageNumber(value) {
  const raw = Array.isArray(value) ? value[0] : value;
  const parsed = Number.parseInt(String(raw || "1"), 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
}

function usesLegacyArchivePath(councilSlug) {
  return !councilSlug || isLegacyCouncil(councilSlug);
}

/**
 * Canonical archive path. Edinburgh keeps /planning-archive[/N]; every other council gets its own
 * crawlable, paginated archive at /planning-archive/<shortCode>[/N] so each application is reachable
 * through plain links and not only through the sitemap.
 */
export function buildPlanningArchivePath(pageNumber = 1, councilSlug = PLANNING_ARCHIVE_DEFAULT_COUNCIL) {
  const page = parsePlanningArchivePageNumber(pageNumber);
  if (usesLegacyArchivePath(councilSlug)) {
    return page <= 1 ? "/planning-archive" : `/planning-archive/${page}`;
  }
  const config = getCouncilConfig(councilSlug);
  const base = `/planning-archive/${config.shortCode}`;
  return page <= 1 ? base : `${base}/${page}`;
}

/** Scottish councils that publish an archive (the same set the public hubs and sitemaps use). */
export function listPlanningArchiveCouncils() {
  return Object.values(COUNCILS)
    .filter((council) => council.publicEnabled)
    .sort((left, right) => left.displayName.localeCompare(right.displayName));
}

/**
 * Resolve the optional catch-all segments of /planning-archive/[[...page]].
 * Returns { council, page } for a renderable archive, { redirect } for a non-canonical spelling,
 * or { notFound: true }.
 */
export function resolvePlanningArchiveRoute(segments) {
  const parts = (Array.isArray(segments) ? segments : segments ? [segments] : []).map(String);
  const isPage = (value) => /^\d+$/.test(value);

  if (parts.length === 0 || (parts.length === 1 && isPage(parts[0]))) {
    return {
      council: COUNCILS[PLANNING_ARCHIVE_DEFAULT_COUNCIL],
      page: parsePlanningArchivePageNumber(parts[0]),
    };
  }

  if (parts.length > 2 || (parts.length === 2 && !isPage(parts[1]))) return { notFound: true };

  const council = getCouncilConfig(parts[0].toLowerCase());
  if (!council || !COUNCILS[council.slug] || !council.publicEnabled) return { notFound: true };

  const page = parts.length === 2 ? Number.parseInt(parts[1], 10) : 1;
  if (!Number.isSafeInteger(page) || page < 1) return { notFound: true };

  const canonicalPath = buildPlanningArchivePath(page, council.slug);
  const requestedPath = `/planning-archive/${parts.join("/")}`;
  // /planning-archive/fife, /planning-archive/fif/1 and the legacy Edinburgh spelling all collapse to one URL.
  if (requestedPath !== canonicalPath) return { redirect: canonicalPath };
  return { council, page };
}
